import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts';
import type { FinancialForecast } from '@crm/shared';

interface ForecastChartProps {
  data: FinancialForecast;
}

function formatCurrency(value: number): string {
  if (value >= 1000) {
    return `R$${(value / 1000).toFixed(1)}k`;
  }
  return `R$${value.toFixed(0)}`;
}

export function ForecastChart({ data }: ForecastChartProps) {
  const chartData = [
    { month: 'Atual', mrr: data.currentMrr, subscribers: data.currentSubscribers },
    ...data.projection,
  ];

  return (
    <div className="bg-white rounded-xl border border-secondary-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-secondary-700">Forecast de Receita</h3>
        <div className="flex gap-4 text-xs text-secondary-500">
          <span>Crescimento: +{data.avgMonthlyGrowth}/mes</span>
          <span>Churn: {(data.avgMonthlyChurnRate * 100).toFixed(1)}%/mes</span>
          <span>ARPU: R${data.arpu.toFixed(2)}</span>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="mrr" tickFormatter={formatCurrency} tick={{ fontSize: 11 }} />
          <YAxis yAxisId="subs" orientation="right" tick={{ fontSize: 11 }} />
          <Tooltip
            formatter={(value, name) => [
              name === 'mrr' ? `R$${Number(value).toFixed(2)}` : value,
              name === 'mrr' ? 'MRR Projetado' : 'Assinantes',
            ]}
          />
          <Legend />
          <ReferenceLine yAxisId="mrr" y={data.currentMrr} stroke="#94A3B8" strokeDasharray="3 3" />
          <Line
            yAxisId="mrr"
            type="monotone"
            dataKey="mrr"
            name="MRR Projetado"
            stroke="#3B82F6"
            strokeWidth={2}
            strokeDasharray="8 4"
            dot={{ r: 3 }}
          />
          <Line
            yAxisId="subs"
            type="monotone"
            dataKey="subscribers"
            name="Assinantes"
            stroke="#10B981"
            strokeWidth={2}
            strokeDasharray="8 4"
            dot={{ r: 3 }}
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Projection table */}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-xs text-secondary-600">
          <thead>
            <tr className="border-b border-secondary-200">
              <th className="text-left py-2 font-medium">Mes</th>
              <th className="text-right py-2 font-medium">Assinantes</th>
              <th className="text-right py-2 font-medium">MRR Projetado</th>
            </tr>
          </thead>
          <tbody>
            {data.projection.map((p) => (
              <tr key={p.month} className="border-b border-secondary-100">
                <td className="py-1.5">{p.month}</td>
                <td className="text-right">{p.subscribers}</td>
                <td className="text-right">R${p.mrr.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
